/** JEDNO API dla online i offline (wymóg założeń SmartLight).
 *
 * Aplikacja woła wyłącznie `osdm(path, body)` — kształty żądań i odpowiedzi są
 * kształtami SaleLighta. O trasie decyduje TEN moduł (czysty TS, testowalny bez
 * service workera; SW dba tylko o start powłoki offline): tryb ONLINE idzie
 * siecią do SaleLight, OFFLINE — do lokalnego silnika na kompilacie.
 * Przełącznik „Wymuś offline" (wymóg prototypu) jest tu, nie w UI. */

import { kv } from "./db";
import { ageFromBirthDate, Bundle, Passenger, quoteOffline } from "./engine";
import { bundleFreshness, limitDecision, LimitAllocation } from "./policy";
import * as sale from "./sale";

export interface Config {
  saleLightUrl: string;
  apiKey: string;
  deviceId: string;
  conductor?: { uzyt: number; name: string | null };
}

let forcedOffline = false;
export function forceOffline(v: boolean): void { forcedOffline = v; }
export function isOffline(): boolean { return forcedOffline || !navigator.onLine; }
export function isForcedOffline(): boolean { return forcedOffline; }

export async function config(): Promise<Config | undefined> {
  return kv.get<Config>("config");
}

export async function online<T>(
  path: string, opts: { method?: string; body?: unknown; etag?: string } = {},
): Promise<{ status: number; etag?: string; data: T | null }> {
  const cfg = await config();
  if (!cfg) throw new Error("urządzenie nieskonfigurowane");
  const headers: Record<string, string> = { "X-Api-Key": cfg.apiKey };
  if (opts.body !== undefined) headers["Content-Type"] = "application/json";
  if (opts.etag) headers["If-None-Match"] = opts.etag;
  const resp = await fetch(cfg.saleLightUrl + path, {
    method: opts.method || (opts.body !== undefined ? "POST" : "GET"),
    headers, body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  const etag = resp.headers.get("etag") ?? undefined;
  if (resp.status === 304) return { status: 304, etag, data: null };
  const data = resp.status === 204 ? null : await resp.json();
  if (!resp.ok) throw new Error((data as { detail?: string })?.detail || `HTTP ${resp.status}`);
  return { status: resp.status, etag, data: data as T };
}

interface OffersBody {
  from_uic: string; to_uic: string; train_id?: string | null;
  travel_at?: string | null; sale_at?: string | null;
  passengers: Passenger[];
  /** nogi podróży w kształcie SaleLighta (snake_case) — z RICS przewoźnika nogi */
  legs?: { from_uic: string; to_uic: string;
    carrier_rics?: string | null; train_number?: string | null }[];
}

/** Punkt wejścia aplikacji — ta sama ścieżka i body co SaleLight `/osdm/*`. */
export async function osdm(path: string, body: unknown): Promise<unknown> {
  // Sprzedaż urządzenia jest LOKALNA z natury (numer z puli, podpis kluczem
  // urządzenia, ogniwo łańcucha) niezależnie od trybu — synchronizacja i tak
  // rejestruje ją w SaleLight. Tryb online/offline steruje wyceną i syncem,
  // nie tym, kto wystawia bilet.
  if (path === "/osdm/offline-fulfill") {
    const req = body as sale.SellRequest;
    // Ta sama reguła co przy wycenie: ładunek sprzedaży niesie listę pasażerów do
    // SaleLighta i po synchronizacji z niej powstaje zapis na bilecie. Bez tego
    // kroku bilet mówiłby „wiek nieznany" przy cenie ulgowej policzonej z daty —
    // przeliczenie jest idempotentne, bo `age` już ustawiony nie jest nadpisywany.
    ageFromBirthDate(req.passengers, req.travelAt);
    return sale.sellOffline(req);
  }
  if (!isOffline()) {
    return (await online(path, { body })).data;
  }
  // ── OFFLINE: lokalne odpowiedniki tych samych ścieżek ──
  if (path === "/osdm/offers") {
    const bundle = await kv.get<Bundle>("bundle");
    if (!bundle) throw new Error("brak kompilatu taryfy — zsynchronizuj urządzenie online");
    const b = body as OffersBody;
    const saleAt = new Date(); // offline: moment sprzedaży = teraz urządzenia
    // Próg wieku pakietu ustalony przez Zamawiającego: po jego przekroczeniu
    // sprzedaż bez sieci STOI. Bramka jest przy wycenie, bo bez propozycji nie
    // ma czego sprzedać — ta sama reguła stoi jeszcze raz przy wydaniu biletu.
    const fresh = bundleFreshness(bundle.compiled_at, bundle.offline_policy, saleAt);
    if (fresh.state === "blocked") throw new Error(fresh.reason!);
    const q = quoteOffline(bundle, {
      fromUic: b.from_uic, toUic: b.to_uic, trainNumber: b.train_id,
      passengers: b.passengers, saleAt, travelAt: b.travel_at,
      // nogi z przewoźnikami — ten sam kształt, co online (honorowanie u innego
      // przewoźnika obowiązuje offline dokładnie tak samo, reguły są w kompilacie)
      legs: (b.legs ?? []).map((l) => ({
        fromUic: l.from_uic, toUic: l.to_uic,
        carrierRics: l.carrier_rics ?? null, trainNumber: l.train_number ?? null,
      })),
    });
    // Limity sprzedaży wspólne dla wielu kanałów: bez przydzielonej urządzeniu
    // rozłącznej części nie wolno ich ruszyć bez łączności — urządzenie nie
    // widzi, ile zdjęły pozostałe kanały. Odmowa idzie do `unavailable`, czyli
    // tam, gdzie kasjer czyta pozostałe powody braku oferty.
    const allocations = await kv.get<LimitAllocation[]>("limit_allocations");
    const travelDay = (b.travel_at || "").slice(0, 10) || null;
    const unavailable = [...q.unavailable];
    const dopuszczone = q.offers.filter((o) => {
      const d = limitDecision(bundle.sale_limits, allocations, {
        offerCode: o.code, units: o.tickets.length, trainNumber: b.train_id ?? null,
        travelDay, fromUic: b.from_uic, toUic: b.to_uic, now: saleAt,
      });
      if (d.reason) unavailable.push({ code: o.code, name: o.name, reason: d.reason });
      return d.reason === null;
    });
    return {
      offers: dopuszczone.map((o) => ({
        offerId: `local:${o.code}`,
        offerSummary: { code: o.code, name: o.name, localOfferId: o.localOfferId,
          ticketType: o.ticketType, ticketCount: o.tickets.length },
        price: { amountGr: o.totalGr, amount: o.totalGr / 100, currency: "PLN" },
        farePriceGr: o.totalGr, lines: o.lines, tickets: o.tickets,
        validityHours: o.validityHours, validityUntil: o.validityUntil,
        honouredLegs: o.honouredLegs.length ? o.honouredLegs : null,
      })),
      unavailable,
      distanceKm: q.distanceKm,
      saleClock: { at: saleAt.toISOString(), simulated: false, offline: true },
      // wiek pakietu przy KAŻDEJ wycenie — ostrzeżenie ma dojść do kasjera,
      // zanim próg przetnie mu zmianę, a nie dopiero przy odmowie
      bundleFreshness: { ageHours: fresh.ageHours, maxAgeHours: fresh.maxAgeHours,
        state: fresh.state, warning: fresh.reason },
      ticketSets: [], passengerMixes: [],
      // ten sam KLUCZ co online (`osdmIgnored.passengerDateOfBirth`) — druga nazwa
      // na to samo zmusiłaby czytającego do pamiętania, która jest w którym trybie
      ...(q.ageNotDerived.length
        ? { osdmIgnored: { passengerDateOfBirth: q.ageNotDerived } }
        : {}),
    };
  }
  throw new Error(`ścieżka ${path} nie ma odpowiednika offline`);
}
